import { QBankStructureLevelCategory } from '@exam-management/graphql';
import { TableBody } from '@shared/components';
import { useCallback, useState } from 'react';

import { DashboardTableRow, SummaryTableRow } from '../../components';
import { computeExpansionState } from '../../utils';
import { DashboardSingleSubTopicTableRow } from '../dashboard-single-sub-topic-table-row';
import { type QBankStructureSingleQuestionTableData } from '../qbank-structure-single-question-dashboard-table';

interface QBankStructureSingleQuestionTableBodyProps {
  rows: QBankStructureSingleQuestionTableData[];
  defaultLevel: QBankStructureLevelCategory;
}

export default function QBankStructureSingleQuestionTableBody(
  props: Readonly<QBankStructureSingleQuestionTableBodyProps>,
) {
  const { rows, defaultLevel } = props;

  const parentIdSet = new Set(
    rows.filter((row) => row.level === defaultLevel).flatMap((row) => row.parentIds ?? []),
  );
  const [openIds, setOpenIds] = useState<Set<string>>(parentIdSet);

  const toggleItem = useCallback((id: string) => {
    setOpenIds((prev) => {
      const updatedSet = new Set(prev);
      if (updatedSet.has(id)) {
        updatedSet.delete(id);
      } else {
        updatedSet.add(id);
      }
      return updatedSet;
    });
  }, []);

  const courseTableRow = rows.find((row) => row.level === QBankStructureLevelCategory.Course);

  return (
    <TableBody>
      {rows.map((row) => {
        const { id, parentIds, level } = row;
        const { isParentExpanded, isExpanded } = computeExpansionState(id, openIds, parentIds);
        if (!isParentExpanded) {
          return null;
        }
        switch (level) {
          case QBankStructureLevelCategory.Part:
            return (
              <DashboardTableRow
                key={id}
                data={{ ...row, level }}
                expanded={isExpanded}
                onExpand={toggleItem}
                totalBoxes={0}
              />
            );
          case QBankStructureLevelCategory.Subject:
            return (
              <DashboardTableRow
                key={id}
                data={{ ...row, level }}
                expanded={isExpanded}
                onExpand={toggleItem}
                totalBoxes={1}
              />
            );
          case QBankStructureLevelCategory.Group:
            return (
              <DashboardTableRow
                key={id}
                data={{ ...row, level }}
                expanded={isExpanded}
                onExpand={toggleItem}
                totalBoxes={2}
              />
            );
          case QBankStructureLevelCategory.Chapter:
            return (
              <DashboardTableRow
                key={id}
                data={{ ...row, level }}
                expanded={isExpanded}
                onExpand={toggleItem}
                totalBoxes={3}
              />
            );
          case QBankStructureLevelCategory.Topic:
            return (
              <DashboardTableRow
                key={id}
                data={{ ...row, level }}
                expanded={isExpanded}
                onExpand={toggleItem}
                totalBoxes={4}
              />
            );
          case QBankStructureLevelCategory.SubTopic:
            return (
              <DashboardSingleSubTopicTableRow
                key={id}
                data={{ ...row, level }}
                expanded={isExpanded}
                onExpand={toggleItem}
                totalBoxes={5}
              />
            );
          default:
            return null;
        }
      })}
      {courseTableRow ? <SummaryTableRow data={courseTableRow} /> : null}
    </TableBody>
  );
}
